/**
 * Custom hook that encapsulates all data-fetching side effects
 * for the RecordPaymentModal.
 */

import { useState, useEffect } from 'react'

import { listAccounts, type Account } from '../../services/account-service'
import { listBankAccounts, type BankAccount } from '../../services/bank-account-service'
import { getExchangeRate } from '../../services/currency-service'
import { getParty } from '../../services/party-service'
import { getEntityBalance } from '../../services/reporting-service'
import { getCurrencyConfig, toSmallestUnit, fromSmallestUnit } from '../../utils/formatters'

export interface UsePaymentFormDataArgs {
  isOpen: boolean
  organizationId: string
  ledgerId: string
  partyId: string
  invoiceType: string
  isForeignCurrency: boolean
  invoiceCurrency: string
  baseCurrency: string
  outstandingBalance: number
}

export interface UsePaymentFormDataResult {
  bankAccounts: BankAccount[]
  cashBankGLAccounts: Account[]
  expenseAccounts: Account[]
  tdsAccounts: Account[]
  isDataLoading: boolean
  isExchangeRateLoading: boolean
  advanceBalance: number
  advanceAccountCode: string
  advanceAccountName: string
  advanceApplied: number | null
  setAdvanceApplied: React.Dispatch<React.SetStateAction<number | null>>
  receivableAccountCode: string
  receivableAccountName: string
  paymentExchangeRate: string
  setPaymentExchangeRate: React.Dispatch<React.SetStateAction<string>>
  feeAccountId: string
  setFeeAccountId: React.Dispatch<React.SetStateAction<string>>
  tdsAccountId: string
  setTdsAccountId: React.Dispatch<React.SetStateAction<string>>
  defaultBankAccountId: string
}

type AccountSetters = {
  setBankAccounts: (v: BankAccount[]) => void
  setCashBankGLAccounts: (v: Account[]) => void
  setExpenseAccounts: (v: Account[]) => void
  setTdsAccounts: (v: Account[]) => void
  setFeeAccountId: (v: string) => void
  setTdsAccountId: (v: string) => void
  setDefaultBankAccountId: (v: string) => void
  setDataLoading: (v: boolean) => void
}

async function loadAccountData(
  organizationId: string, ledgerId: string, isVendorBill: boolean, setters: AccountSetters
): Promise<void> {
  setters.setDataLoading(true)
  const [bankRes, assetRes, expenseRes, liabilityRes] = await Promise.all([
    listBankAccounts(organizationId, { ledgerId, activeOnly: true }),
    listAccounts(organizationId, { accountType: 'ACCOUNT_TYPE_ASSET', includeHeaders: false }, 200),
    listAccounts(organizationId, { accountType: 'ACCOUNT_TYPE_EXPENSE', includeHeaders: false }, 200),
    listAccounts(organizationId, { accountType: 'ACCOUNT_TYPE_LIABILITY', includeHeaders: false }, 200),
  ])
  if (bankRes.data?.bankAccounts) {
    setters.setBankAccounts(bankRes.data.bankAccounts)
    const def = bankRes.data.bankAccounts.find(a => a.isDefault)
    if (def) { setters.setDefaultBankAccountId(`bank:${def.id}`) }
  }
  if (assetRes.data?.accounts) {
    const cashBank = assetRes.data.accounts.filter(a =>
      a.isActive && !a.isHeader && /bank/i.test(a.accountSubtype || '')
    )
    setters.setCashBankGLAccounts(cashBank)
  }
  if (expenseRes.data?.accounts) {
    const leafExpense = expenseRes.data.accounts.filter(a => a.isActive && !a.isHeader)
    setters.setExpenseAccounts(leafExpense)
    const bankFeeAcc = leafExpense.find(a => a.systemTag === 'bank_fee')
    if (bankFeeAcc) { setters.setFeeAccountId(bankFeeAcc.id) }
  }
  const allTdsAccounts: Account[] = []
  if (assetRes.data?.accounts) { allTdsAccounts.push(...assetRes.data.accounts.filter(a => a.isActive && !a.isHeader)) }
  if (liabilityRes.data?.accounts) { allTdsAccounts.push(...liabilityRes.data.accounts.filter(a => a.isActive && !a.isHeader)) }
  setters.setTdsAccounts(allTdsAccounts)
  const defaultCode = isVendorBill ? '2130' : '1420'
  const defaultTds = allTdsAccounts.find(a => a.code === defaultCode)
  if (defaultTds) { setters.setTdsAccountId(defaultTds.id) }
  setters.setDataLoading(false)
}

type PartySetters = {
  setRecvAccountCode: (v: string) => void
  setRecvAccountName: (v: string) => void
  setAdvanceBalance: (v: number) => void
  setAdvanceAccountCode: (v: string) => void
  setAdvanceAccountName: (v: string) => void
  setAdvanceApplied: (v: number | null) => void
}

async function loadReceivableAccount(
  organizationId: string,
  partyAccountId: string,
  isVendorBill: boolean,
  setters: PartySetters,
): Promise<void> {
  const accountType = isVendorBill ? 'ACCOUNT_TYPE_LIABILITY' : 'ACCOUNT_TYPE_ASSET'
  const accRes = await listAccounts(organizationId, { accountType, includeHeaders: false }, 200)
  if (!accRes.data?.accounts) {return}
  const acct = accRes.data.accounts.find(a => a.id === partyAccountId)
  if (acct) {setters.setRecvAccountCode(acct.code); setters.setRecvAccountName(acct.name)}
}

interface AdvanceLoadParams {
  organizationId: string
  entityId: string
  isVendorBill: boolean
  invoiceCurrency: string
  outstandingBalance: number
  setters: PartySetters
}

async function loadAdvanceBalance(p: AdvanceLoadParams): Promise<void> {
  const balRes = await getEntityBalance(p.organizationId, p.entityId)
  if (!balRes.data?.rows) {return}
  const advanceCode = p.isVendorBill ? '1650' : '2760'
  const advanceLabel = p.isVendorBill ? 'Vendor Advance' : 'Customer Advance'
  const advRow = balRes.data.rows.find(r =>
    r.accountCode?.startsWith(advanceCode) ?? r.accountName?.toLowerCase().includes(advanceLabel.toLowerCase())
  )
  if (!advRow?.balance) {return}
  const bal = Number(advRow.balance.amount) ?? 0
  if (bal <= 0) {return}
  p.setters.setAdvanceBalance(bal)
  p.setters.setAdvanceAccountCode(advRow.accountCode ?? advanceCode)
  p.setters.setAdvanceAccountName(advRow.accountName ?? advanceLabel)
  const maxApplicable = Math.min(bal, toSmallestUnit(p.outstandingBalance, p.invoiceCurrency))
  p.setters.setAdvanceApplied(fromSmallestUnit(maxApplicable, p.invoiceCurrency))
}

// eslint-disable-next-line max-params -- TODO(refactor): convert to options object
async function loadPartyData(
  organizationId: string, partyId: string, isVendorBill: boolean,
  invoiceCurrency: string, outstandingBalance: number, setters: PartySetters
): Promise<void> {
  const res = await getParty(organizationId, partyId)
  if (!res.data) {return}
  const party = res.data
  const partyAccountId = isVendorBill ? party.payableAccountId : party.receivableAccountId
  if (partyAccountId) {
    await loadReceivableAccount(organizationId, partyAccountId, isVendorBill, setters)
  }
  if (party.entityId) {
    await loadAdvanceBalance({
      organizationId, entityId: party.entityId, isVendorBill,
      invoiceCurrency, outstandingBalance, setters,
    })
  }
}

interface UseExchangeRateAutofillParams {
  isOpen: boolean
  isForeignCurrency: boolean
  invoiceCurrency: string
  baseCurrency: string
  setPaymentExchangeRate: React.Dispatch<React.SetStateAction<string>>
  setExchangeRateLoading: React.Dispatch<React.SetStateAction<boolean>>
}

function useExchangeRateAutofill(p: UseExchangeRateAutofillParams): void {
  const {
    isOpen, isForeignCurrency, invoiceCurrency, baseCurrency,
    setPaymentExchangeRate, setExchangeRateLoading,
  } = p
  useEffect(() => {
    if (!isOpen || !isForeignCurrency) {setPaymentExchangeRate(''); return}
    let isCancelled = false
    setExchangeRateLoading(true)
    void getExchangeRate(invoiceCurrency, baseCurrency)
      .then(result => {
        if (!isCancelled && result.data) {
          const decimals = getCurrencyConfig(baseCurrency).decimalPlaces
          setPaymentExchangeRate(result.data.rate.toFixed(decimals + 2))
        }
      })
      .finally(() => { if (!isCancelled) {setExchangeRateLoading(false)} })
    return () => { isCancelled = true }
  }, [isOpen, isForeignCurrency, invoiceCurrency, baseCurrency, setPaymentExchangeRate, setExchangeRateLoading])
}

export function usePaymentFormData({
  isOpen, organizationId, ledgerId, partyId, invoiceType, isForeignCurrency,
  invoiceCurrency, baseCurrency, outstandingBalance,
}: UsePaymentFormDataArgs): UsePaymentFormDataResult {
  const isVendorBill = invoiceType === 'vendor_bill' || invoiceType === 'vendor_credit_note'

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [cashBankGLAccounts, setCashBankGLAccounts] = useState<Account[]>([])
  const [expenseAccounts, setExpenseAccounts] = useState<Account[]>([])
  const [tdsAccounts, setTdsAccounts] = useState<Account[]>([])
  const [isDataLoading, setDataLoading] = useState(true)
  const [isExchangeRateLoading, setExchangeRateLoading] = useState(false)
  const [advanceBalance, setAdvanceBalance] = useState(0)
  const [advanceAccountCode, setAdvanceAccountCode] = useState(isVendorBill ? '1650' : '2760')
  const [advanceAccountName, setAdvanceAccountName] = useState(isVendorBill ? 'Vendor Advance' : 'Customer Advance')
  const [advanceApplied, setAdvanceApplied] = useState<number | null>(null)
  const [receivableAccountCode, setRecvAccountCode] = useState(isVendorBill ? '2100' : '1200')
  const [receivableAccountName, setRecvAccountName] = useState(isVendorBill ? 'Trade Payable' : 'Trade Receivable')
  const [paymentExchangeRate, setPaymentExchangeRate] = useState('')
  const [feeAccountId, setFeeAccountId] = useState('')
  const [tdsAccountId, setTdsAccountId] = useState('')
  const [defaultBankAccountId, setDefaultBankAccountId] = useState('')

  useEffect(() => {
    if (!isOpen || !organizationId || !ledgerId) {return}
    void loadAccountData(organizationId, ledgerId, isVendorBill, {
      setBankAccounts, setCashBankGLAccounts, setExpenseAccounts, setTdsAccounts,
      setFeeAccountId, setTdsAccountId, setDefaultBankAccountId, setDataLoading,
    })
  }, [isOpen, organizationId, ledgerId, isVendorBill])

  useEffect(() => {
    if (!isOpen || !organizationId || !partyId) {return}
    void loadPartyData(organizationId, partyId, isVendorBill, invoiceCurrency, outstandingBalance, {
      setRecvAccountCode, setRecvAccountName, setAdvanceBalance,
      setAdvanceAccountCode, setAdvanceAccountName, setAdvanceApplied,
    })
  }, [isOpen, organizationId, partyId, invoiceCurrency, isVendorBill, outstandingBalance])

  useExchangeRateAutofill({
    isOpen, isForeignCurrency, invoiceCurrency, baseCurrency,
    setPaymentExchangeRate, setExchangeRateLoading,
  })

  return {
    bankAccounts, cashBankGLAccounts, expenseAccounts, tdsAccounts,
    isDataLoading, isExchangeRateLoading, advanceBalance,
    advanceAccountCode, advanceAccountName, advanceApplied, setAdvanceApplied,
    receivableAccountCode, receivableAccountName, paymentExchangeRate, setPaymentExchangeRate,
    feeAccountId, setFeeAccountId, tdsAccountId, setTdsAccountId, defaultBankAccountId,
  }
}
